import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Clock, ArrowRight, Check, Loader2, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { supabase } from '../lib/supabase';
import { useNewOrderNotifier } from '../hooks/useNewOrderNotifier';

const COLUMNS = [
  { key: 'new', title: 'Nieuw', accent: 'border-l-amber-400', badge: 'bg-amber-400/20 text-amber-300' },
  { key: 'preparing', title: 'In bereiding', accent: 'border-l-blue-400', badge: 'bg-blue-400/20 text-blue-300' },
  { key: 'ready', title: 'Klaar', accent: 'border-l-green-500', badge: 'bg-green-500/20 text-green-300' },
];

const NEXT_STATUS = {
  new: 'preparing',
  preparing: 'ready',
  ready: 'picked_up',
};

const NEXT_LABEL = {
  new: 'Start bereiding',
  preparing: 'Markeer als klaar',
  ready: 'Opgehaald',
};

const shortId = (id) => (id ? id.substring(0, 6).toUpperCase() : '------');

const minutesAgo = (iso) => {
  if (!iso) return 0;
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  return Math.max(0, Math.floor(diff));
};

const ItemLine = ({ item, flavorMap }) => {
  const meta = [];
  if (item.flavor_id && flavorMap[item.flavor_id]) meta.push(flavorMap[item.flavor_id]);
  if (item.preparation) meta.push(item.preparation);

  const displayName =
    item.name ||
    item.products?.name ||
    item.sides?.name ||
    (item.item_type === 'burger' ? 'Burger upgrade' : 'Item');

  return (
    <li className="flex items-baseline gap-2 text-foreground" data-testid={`kds-item-${item.item_type}`}>
      <span className="text-2xl font-bold text-primary min-w-[2rem]">{item.quantity}x</span>
      <span className="flex-1">
        <span className="text-xl font-semibold">{displayName}</span>
        {meta.length > 0 && (
          <span className="block text-base text-muted-foreground mt-0.5">{meta.join(' · ')}</span>
        )}
      </span>
    </li>
  );
};

const OrderCard = ({ order, flavorMap, onAdvance, busy, flash }) => {
  const items = order.order_items || [];
  // Sort: main → burger → side
  const order_rank = { main: 0, burger: 1, side: 2 };
  const sortedItems = [...items].sort((a, b) => (order_rank[a.item_type] ?? 9) - (order_rank[b.item_type] ?? 9));
  const accent = COLUMNS.find(c => c.key === order.kitchen_status)?.accent || '';
  const elapsed = minutesAgo(order.created_at);
  const urgent = elapsed >= 10;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
    >
      <Card
        data-testid={`kds-order-card-${order.id}`}
        className={`p-5 bg-card border-l-4 ${accent} border-y border-r border-border/60 shadow-md transition-all ${
          flash ? 'ring-4 ring-primary/60 shadow-[0_0_30px_hsl(45_100%_51%/0.5)] animate-pulse' : ''
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-3xl font-bold font-mono text-primary tracking-wider" data-testid="kds-order-number">
            #{shortId(order.id)}
          </span>
          <span className={`flex items-center gap-1.5 text-base font-semibold ${urgent ? 'text-red-400' : 'text-muted-foreground'}`}>
            <Clock className="w-4 h-4" />
            {elapsed} min
          </span>
        </div>

        <ul className="space-y-2 mb-5">
          {sortedItems.map(it => <ItemLine key={it.id} item={it} flavorMap={flavorMap} />)}
        </ul>

        <Button
          onClick={() => onAdvance(order)}
          disabled={busy}
          size="lg"
          data-testid={`kds-advance-btn-${order.id}`}
          className="w-full text-lg font-semibold py-6 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>
              {order.kitchen_status === 'ready' ? <Check className="w-5 h-5 mr-2" /> : <ArrowRight className="w-5 h-5 mr-2" />}
              {NEXT_LABEL[order.kitchen_status]}
            </>
          )}
        </Button>
      </Card>
    </motion.div>
  );
};

const Column = ({ column, orders, flavorMap, onAdvance, busyId, flashIds }) => (
  <div className="flex-1 min-w-0 flex flex-col">
    <div className="sticky top-0 z-10 bg-background pb-3 mb-3 border-b-2 border-border">
      <div className="flex items-baseline justify-between">
        <h2 className="text-2xl font-bold text-foreground">{column.title}</h2>
        <span className={`px-3 py-1 rounded-full text-sm font-bold ${column.badge}`} data-testid={`kds-column-count-${column.key}`}>
          {orders.length}
        </span>
      </div>
    </div>
    <div className="space-y-4 overflow-y-auto pr-1 pb-6" data-testid={`kds-column-${column.key}`}>
      <AnimatePresence>
        {orders.map(o => (
          <OrderCard
            key={o.id}
            order={o}
            flavorMap={flavorMap}
            onAdvance={onAdvance}
            busy={busyId === o.id}
            flash={flashIds.has(o.id)}
          />
        ))}
      </AnimatePresence>
      {orders.length === 0 && (
        <p className="text-muted-foreground text-center py-8 text-sm">Geen bestellingen</p>
      )}
    </div>
  </div>
);

export const KitchenDisplay = () => {
  const [orders, setOrders] = useState([]);
  const [flavorMap, setFlavorMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tick, setTick] = useState(0); // refresh "X min ago" every minute

  // Keep elapsed minutes live
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const fetchOrders = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('orders')
      .select('id, created_at, total_price, payment_status, kitchen_status, order_items(*, products(name), sides(name))')
      .eq('payment_status', 'paid')
      .neq('kitchen_status', 'picked_up')
      .order('created_at', { ascending: true });

    if (err) {
      setError('Kon bestellingen niet laden');
      setLoading(false);
      return;
    }
    setOrders(data || []);
    setError(null);
    setLoading(false);
  }, []);

  const fetchFlavors = useCallback(async () => {
    const { data } = await supabase.from('flavors').select('id, name');
    if (data) setFlavorMap(Object.fromEntries(data.map(f => [f.id, f.name])));
  }, []);

  useEffect(() => {
    fetchFlavors();
    fetchOrders();

    // Realtime subscription: any change to orders triggers refetch
    const channel = supabase
      .channel('kds-orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, fetchOrders)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, fetchOrders)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchOrders, fetchFlavors]);

  const handleAdvance = async (order) => {
    const next = NEXT_STATUS[order.kitchen_status];
    if (!next) return;
    setBusyId(order.id);
    const { error: err } = await supabase
      .from('orders')
      .update({ kitchen_status: next })
      .eq('id', order.id);
    if (err) setError('Statusupdate mislukt');
    setBusyId(null);
    // Optimistic update — realtime will reconcile
    setOrders(prev =>
      next === 'picked_up'
        ? prev.filter(o => o.id !== order.id)
        : prev.map(o => o.id === order.id ? { ...o, kitchen_status: next } : o)
    );
  };

  const grouped = useMemo(() => {
    const g = { new: [], preparing: [], ready: [] };
    orders.forEach(o => { if (g[o.kitchen_status]) g[o.kitchen_status].push(o); });
    return g;
  }, [orders, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const { soundEnabled, toggleSound, testSound, flashIds } = useNewOrderNotifier(orders);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border px-6 py-4 flex items-center gap-3">
        <ChefHat className="w-8 h-8 text-primary" />
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">Kitchen Display</h1>
        <span className="ml-auto text-sm text-muted-foreground" data-testid="kds-total-count">
          {orders.length} actieve bestellingen
        </span>
        <button
          onClick={soundEnabled ? toggleSound : () => { toggleSound(); testSound(); }}
          data-testid="kds-sound-toggle"
          className={`ml-3 p-2 rounded-lg border transition-colors ${
            soundEnabled
              ? 'border-primary/50 bg-primary/10 text-primary hover:bg-primary/20'
              : 'border-border text-muted-foreground hover:bg-muted/30'
          }`}
          title={soundEnabled ? 'Geluid aan (klik om uit te zetten)' : 'Geluid uit (klik om aan te zetten)'}
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
      </header>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 mx-6 mt-3 rounded flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="px-6 pt-4 pb-2 flex gap-6 h-[calc(100vh-80px)]">
        {COLUMNS.map(col => (
          <Column
            key={col.key}
            column={col}
            orders={grouped[col.key] || []}
            flavorMap={flavorMap}
            onAdvance={handleAdvance}
            busyId={busyId}
            flashIds={flashIds}
          />
        ))}
      </div>
    </div>
  );
};

export default KitchenDisplay;
